import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { IconButton } from '@/components/IconButton';
import { DeleteIcon } from '@/components/Icons';

import {
	Box,
	CardContent,
	CardHeader,
	Stack,
	cardHeaderClasses
} from '@mui/material';
import { usePaymentMethod, useSubscribed, useSubscriptionInfo } from '@/hooks';
import { paymentMethodLogos, walletLogos } from '@/utils/payment';

import { AlertDeletePaymentMethodModal } from './AlertDeletePaymentMethod';
import { DeletePaymentMethodConfirmation } from './DeletePaymentMethodConfirmation';
import { getPaymentMethodLabel } from './utils';
import { useUserPermissions } from '../../../hooks/hooks';

export function PaymentMethod({ onClickUpdatePayment = () => {} }) {
	const { cancellationRequested, isCanceled, isExpired } =
		useSubscriptionInfo();
	const { info, hasPaymentMethod } = usePaymentMethod();
	const subscribed = useSubscribed();

	const { t } = useTranslation();
	const [alertDeletePaymentOpen, setAlertDeletePaymentOpen] = useState(false);
	const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
	const { getIsAdminOrOwner } = useUserPermissions();

	const walletLogo = walletLogos[info.walletType];

	let paymentMethodIcon = null;
	let paymentMethodAlt = '';

	if (info?.type === 'card') {
		paymentMethodIcon =
			paymentMethodLogos[info.cardBrand] ||
			paymentMethodLogos[info.cardType] ||
			paymentMethodLogos[info.type];
		paymentMethodAlt = info?.cardBrand;
	} else if (info?.type === 'link') {
		paymentMethodIcon = paymentMethodLogos.link;
		paymentMethodAlt = info.linkEmail;
	}

	const canRemovePayment =
		cancellationRequested || isExpired || isCanceled || !subscribed;

	function handleDeletePaymentMethod() {
		if (!canRemovePayment) {
			setAlertDeletePaymentOpen(true);
		} else {
			setDeleteConfirmationOpen(true);
		}
	}

	const showPaymentMethodActions = getIsAdminOrOwner();

	return (
		<>
			<AlertDeletePaymentMethodModal
				open={alertDeletePaymentOpen}
				onClose={() => setAlertDeletePaymentOpen(false)}
			/>
			<DeletePaymentMethodConfirmation
				open={deleteConfirmationOpen}
				onClose={() => setDeleteConfirmationOpen(false)}
			/>
			<Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
				<CardHeader
					title={t('common.paymentMethod')}
					subheader={
						<Box display="flex" alignItems="center" gap="8px">
							{walletLogo && hasPaymentMethod && (
								<Box
									width="48px"
									height="32px"
									component="img"
									src={walletLogo}
									alt={info.walletType}
								/>
							)}

							{hasPaymentMethod && (
								<Box
									width="48px"
									height="32px"
									component="img"
									src={paymentMethodIcon}
									alt={paymentMethodAlt}
								/>
							)}

							{getPaymentMethodLabel(info)}
						</Box>
					}
					sx={{
						[`.${cardHeaderClasses.subheader}`]: {
							mt: '12px'
						}
					}}
				/>
				{showPaymentMethodActions && (
					<CardContent sx={{ mt: 'auto' }}>
						<Stack
							direction="row"
							justifyContent="end"
							spacing={1}
							useFlexGap
						>
							{hasPaymentMethod && (
								<IconButton
									variant="outlined"
									onClick={handleDeletePaymentMethod}
								>
									<DeleteIcon />
								</IconButton>
							)}

							<Button
								variant="outlined"
								onClick={() => {
									onClickUpdatePayment?.();
								}}
							>
								{hasPaymentMethod
									? t('common.update')
									: t('common.add')}
							</Button>
						</Stack>
					</CardContent>
				)}
			</Card>
		</>
	);
}
